import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, KeyRound, Loader2, Smartphone } from "lucide-react";

import { ActionButton, Modal, Notice, cx } from "../account.ui";
import { TribunalGuide } from "./access.connect.modal";
import { codeItemOf, failureCopyOf } from "./codes.helpers";

import filingService from "../../../../domain/services/filing/filing.service";
import { useFilingCustomerId } from "./filing.scope";

// Passo a passo SÓ para desativar o código que o cliente já usa. Telas do manual oficial do PJe (TJMT);
// o PDPJ é o mesmo login para todo PJe. F185: no login do tribunal, só o "Entrar com PDPJ" passa pelo código;
// o link vai direto ao login do TST (o endereço pjekz cai numa página de erro).
const PDPJ_LOGIN_URL = 'https://pje.tst.jus.br/tst/login.seam';

const DEACTIVATION_GUIDES = {
    pje: {
        source: 'Telas do PJe do TST e do manual oficial do PJe (TJMT). O login do PDPJ é o mesmo em todos os tribunais do PJe.',
        setupUrl: PDPJ_LOGIN_URL,
        steps: [
            {
                title: 'Entre pelo PDPJ',
                text: 'Abra o PJe do TST pelo link abaixo e clique em "Entrar com PDPJ". Na tela do PDPJ, entre com "Seu certificado digital", com o PJeOffice aberto. Se o certificado der erro (por exemplo, "HTTP Status 401"), entre com CPF e senha.',
                image: 'pje-1-tst-entrar-com-pdpj.jpg',
            },
            {
                title: 'Peça um novo dispositivo',
                text: 'Na tela que pede o código, clique em "Configurar novo dispositivo" (em alguns, "Configurar novo aplicativo"). Não digite código.',
                image: 'pje-2-configurar-novo-dispositivo.jpg',
            },
            {
                title: 'Abra o e-mail do CNJ',
                text: 'Chega um e-mail do CNJ. Clique no link em até 5 minutos. Se não achar, veja o spam.',
                image: 'pje-3-email-link-5min.jpg',
            },
            {
                title: 'Apareceu o QR Code? Feche a página',
                text: 'Pare nesta tela. Não escaneie o QR Code, não copie a chave, não digite código e não clique em Cadastrar. Feche a página do tribunal e volte para a Facilita.',
                image: 'pje-4-qr-nao-foi-possivel-ler-cadastrar.jpg',
                check: 'O QR Code na imagem é apenas uma referência do ponto em que você deve parar. Ao chegar nessa tela no tribunal, feche-a e clique aqui em "Já desativei". A Facilita faz o novo cadastro para você.',
            },
        ],
    },
};

function Choice({ checked, title, text, onSelect }) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={onSelect}
            className={cx(
                'flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition',
                checked ? 'border-primary bg-primaryTint ring-2 ring-primary' : 'border-[#dcdce2] bg-white hover:border-primary',
            )}
        >
            <span className={cx('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', checked ? 'border-primary' : 'border-[#c4c4cc]')} aria-hidden="true">
                {checked && <span className="h-2 w-2 rounded-full bg-primary" />}
            </span>
            <span className="min-w-0">
                <span className="block text-sm font-semibold text-[#1d1d1f]">{title}</span>
                <span className="mt-0.5 block text-[13px] leading-5 text-[#6b6b76]">{text}</span>
            </span>
        </button>
    );
}

// Conectar um tribunal com ativação pela Facilita (F107): pergunta se já usa código; se usa, mostra como desativar.
// `startAt = 'guide'` abre direto no passo a passo (erro "o código antigo ainda está ligado").
export default function CodeActivateModal({ open, item, startAt = 'question', onClose, onActivated, onReady }) {
    const customerId = useFilingCustomerId();
    const [step, setStep] = useState(startAt === 'guide' ? 'guide' : item?.status === 'activating' || item?.status === 'error' ? 'done' : startAt);
    const [answer, setAnswer] = useState(startAt === 'guide' ? 'yes' : null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    const [activation, setActivation] = useState(item);
    const [refreshKey, setRefreshKey] = useState(0);
    const activationContext = useRef({ item, onReady });
    activationContext.current = { item, onReady };
    const certificateId = item?.certificateId;
    const accessGroup = item?.accessGroup;
    useEffect(() => {
        if (!open || step !== 'done' || activation?.status !== 'activating') return;
        let cancelled = false;
        let timer;
        const refresh = async () => {
            try {
                const settings = await filingService.findSettings({ customerId });
                if (cancelled) return;
                const certificate = settings.lawyers?.map(lawyer => lawyer.certificate)
                    .find(certificate => Number(certificate?.id) === Number(certificateId));
                if (!certificate) throw new Error('CERTIFICATE_NOT_FOUND');
                const updated = codeItemOf({ ...activationContext.current.item, key: accessGroup }, certificate);
                setActivation(updated);
                setError(null);
                if (updated.status === 'active') activationContext.current.onReady?.();
                if (updated.status !== 'activating') return;
            } catch (failure) {
                if (cancelled) return;
                setError('Não conseguimos consultar a ativação. Aguarde ou tente consultar de novo.');
            }
            if (!cancelled) timer = setTimeout(refresh, 10000);
        };
        if (refreshKey) refresh();
        else timer = setTimeout(refresh, 10000);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [open, step, certificateId, accessGroup, activation?.status, refreshKey, customerId]);

    if (!item)
        return null;

    const guide = DEACTIVATION_GUIDES[item.system] || DEACTIVATION_GUIDES.pje;
    const access = { key: item.accessGroup, name: item.name, system: item.system, setupUrl: guide.setupUrl || item.setupUrl };
    const isPdpj = item.system === 'pje';
    const question = isPdpj ? 'Ao entrar no PJe com o PDPJ, ele pede um código do celular?' : 'Você já usa código de verificação neste tribunal?';

    const activate = async (alreadyUsed) => {
        setIsSaving(true);
        setError(null);
        try {
            await filingService.activateAccess({ certificateId: item.certificateId, accessKey: item.accessGroup, alreadyUsed, customerId });
            setActivation({ ...item, status: 'activating', failure: null });
            setStep('done');
            onActivated?.();
        } catch (failure) {
            setError(filingService.errorMessageOf(failure));
        } finally {
            setIsSaving(false);
        }
    };

    if (step === 'done') {
        const ready = activation?.status === 'active';
        const waiting = activation?.status === 'activating';
        const failure = failureCopyOf(activation);
        const retry = () => { setError(null); setStep(failure.fix === 'guide' ? 'guide' : 'question'); };
        return (
            <Modal
                open={open}
                onClose={onClose}
                size="sm"
                title={ready ? 'Código ativo' : waiting ? `Ativando o código de ${item.name}` : failure.title}
                footer={(
                    <>
                        <ActionButton variant={waiting ? 'primary' : 'secondary'} onClick={onClose}>{ready ? 'Concluir' : 'Fechar'}</ActionButton>
                        {!ready && !waiting && failure.fix === 'certificate' && <a href="/codes" className="text-sm font-medium text-primary">Enviar certificado</a>}
                        {!ready && !waiting && !['certificate', 'team'].includes(failure.fix) && <ActionButton onClick={retry}>{failure.label}</ActionButton>}
                        {error && <ActionButton onClick={() => setRefreshKey(key => key + 1)}>Consultar de novo</ActionButton>}
                    </>
                )}
            >
                <div className="flex flex-col items-center gap-3 pb-2 text-center">
                    {ready ? <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" /> : waiting ? <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" /> : <AlertTriangle className="h-8 w-8 text-[#b3261e]" aria-hidden="true" />}
                    <p className="m-0 text-lg font-semibold text-[#1d1d1f]">{ready ? 'Código pronto para usar' : waiting ? 'Estamos ativando o código' : failure.title}</p>
                    <p className="m-0 text-sm leading-6 text-[#6b6b76]">
                        {ready ? 'O acesso ao tribunal está pronto. Se você usava um código no app do celular, ele não vale mais. Para ter o mesmo código no celular, abra Códigos dos tribunais e clique em "Adicionar no meu celular".' : waiting ? 'Você já pode fechar esta janela. A ativação continua sozinha e leva alguns minutos. Avisamos por e-mail quando o código ficar pronto para pedir o protocolo.' : failure.action}
                    </p>
                    {error && <Notice tone="danger">{error}</Notice>}
                </div>
            </Modal>
        );
    }

    const footer = step === 'question'
        ? (
            <>
                <ActionButton variant="secondary" onClick={onClose} disabled={isSaving}>Cancelar</ActionButton>
                {answer === 'yes'
                    ? <ActionButton onClick={() => { setError(null); setStep('guide'); }}>Continuar</ActionButton>
                    : <ActionButton onClick={() => activate(false)} loading={isSaving} disabled={answer !== 'no'}>Ativar</ActionButton>}
            </>
        )
        : (
            <>
                <ActionButton variant="secondary" onClick={() => { setError(null); setStep('question'); }} disabled={isSaving}>Voltar</ActionButton>
                <ActionButton icon={CheckCircle2} onClick={() => activate(true)} loading={isSaving}>Já desativei</ActionButton>
            </>
        );

    return (
        <Modal
            open={open}
            onClose={onClose}
            dismissible={!isSaving}
            icon={KeyRound}
            size={step === 'guide' ? 'lg' : 'md'}
            title={`Conectar ${item.name}`}
            description={step === 'guide' ? 'Desative o código que você usa hoje. Leva uns 3 minutos.' : 'A Facilita ativa o código para você. Depois, ele aparece aqui.'}
            footer={footer}
        >
            {step === 'question' && (
                <div className="flex flex-col gap-3" role="radiogroup" aria-label={question}>
                    <p className="m-0 text-sm font-semibold text-[#1d1d1f]">{question}</p>
                    <Choice
                        checked={answer === 'no'}
                        title="Não uso"
                        text="Não precisa fazer nada. É só clicar em Ativar."
                        onSelect={() => setAnswer('no')}
                    />
                    <Choice
                        checked={answer === 'yes'}
                        title="Já uso"
                        text="Antes, você desativa o código atual. Mostramos como, passo a passo."
                        onSelect={() => setAnswer('yes')}
                    />
                    <p className="m-0 text-xs text-[#8a8a94]">
                        {isPdpj
                            ? <>Na dúvida, abra o <a href={PDPJ_LOGIN_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">PJe do TST</a>, clique em "Entrar com PDPJ" e entre com "Seu certificado digital" ou com CPF e senha. Se ele pedir o código do celular, você usa.</>
                            : 'Na dúvida: se o tribunal pede um código do celular quando você entra, você usa.'}
                    </p>
                </div>
            )}

            {step === 'guide' && (
                <div className="flex flex-col gap-4">
                    <Notice tone="warning" icon={AlertTriangle} title="Só desative se você já usa código">
                        {isPdpj ? 'Se, ao entrar com o PDPJ, o PJe não pede código, você não precisa fazer nada: volte e escolha "Não uso".' : 'Se você não usa código neste tribunal, não precisa fazer nada: volte e escolha "Não uso".'}
                    </Notice>
                    <Notice tone="brand" icon={Smartphone} title="O código do seu celular vai parar de funcionar">
                        Depois da ativação, abra Códigos dos tribunais, clique em "Adicionar no meu celular" e leia o QR Code no app. Assim o mesmo código aparece no celular e aqui.
                    </Notice>
                    <TribunalGuide guide={guide} access={access} />
                </div>
            )}

            {error && <Notice tone="danger" className="mt-4">{error}</Notice>}
        </Modal>
    );
}
